import {
  CLIENT_INTAKE_SUBMITTED_BODY,
  CLIENT_INTAKE_SUBMITTED_HEADLINE,
  PRIMARY_EVIDENCE_TYPE_OPTIONS,
  toPrimaryEvidenceTypeLabel,
} from "@enrein/shared-contracts/display/tier1-client-display";
import type { PrimaryEvidenceType } from "@enrein/shared-contracts";
import { useGreenfieldIntake } from "../adapters/use-greenfield-intake.js";
import type { GreenfieldClientHubProps } from "../types.js";

export function GreenfieldIntakeView({
  active,
  api,
  linkId,
  sessionToken,
  linkStatus,
  onActionStatus,
}: {
  readonly active: boolean;
  readonly api: GreenfieldClientHubProps["api"];
  readonly linkId: string;
  readonly sessionToken: string;
  readonly linkStatus: string;
  readonly onActionStatus: GreenfieldClientHubProps["onActionStatus"];
}) {
  const intake = useGreenfieldIntake({
    api,
    linkId,
    sessionToken,
    linkStatus,
    onStatus: onActionStatus,
  });

  if (!active) return null;

  if (intake.error) {
    return (
      <section className="ws-card" id="gf-tab-intake" role="tabpanel" aria-labelledby="gf-tab-btn-intake">
        <h2 className="ws-title">Business intake</h2>
        <p className="ws-global-notice" role="alert">
          {intake.error}
        </p>
      </section>
    );
  }

  if (!intake.view) {
    return (
      <section className="ws-card" id="gf-tab-intake" role="tabpanel" aria-labelledby="gf-tab-btn-intake" aria-busy="true">
        <h2 className="ws-title">Business intake</h2>
        <p className="ws-muted">Loading your intake form…</p>
      </section>
    );
  }

  if (intake.readOnly) {
    return (
      <section className="ws-card" id="gf-tab-intake" role="tabpanel" aria-labelledby="gf-tab-btn-intake">
        <h2 className="ws-title">{CLIENT_INTAKE_SUBMITTED_HEADLINE}</h2>
        <p role="status">{CLIENT_INTAKE_SUBMITTED_BODY}</p>
        <dl>
          <div className="ws-field">
            <dt>Business name</dt>
            <dd>{intake.view.businessName}</dd>
          </div>
          {intake.view.primaryPhone ? (
            <div className="ws-field">
              <dt>Phone</dt>
              <dd>{intake.view.primaryPhone}</dd>
            </div>
          ) : null}
          {intake.view.primaryEmail ? (
            <div className="ws-field">
              <dt>Email</dt>
              <dd>{intake.view.primaryEmail}</dd>
            </div>
          ) : null}
          {intake.view.description ? (
            <div className="ws-field">
              <dt>Description</dt>
              <dd>{intake.view.description}</dd>
            </div>
          ) : null}
        </dl>
      </section>
    );
  }

  return (
    <section className="ws-card" id="gf-tab-intake" role="tabpanel" aria-labelledby="gf-tab-btn-intake">
      <h2 className="ws-title">Tell us about your business</h2>
      <p className="ws-muted">
        Share the basics your agency needs. Progress saves automatically before you submit.
      </p>
      {intake.draftSaveState === "saved" ? (
        <p className="ws-muted" role="status">
          Draft saved
        </p>
      ) : null}
      {intake.draftSaveState === "error" ? (
        <p className="ws-global-notice" role="status">
          {intake.draftSaveError ?? "Could not save draft. Check your connection and try again."}
        </p>
      ) : null}
      {/*
        Field ids use the shared client-intake DOM contract (`v10-intake-*`) so
        session-resume / draft-flush probes and useIntakeForm stay bound after
        greenfield became the default portal. This is not a V10 visual import.
      */}
      <div className="ws-field">
        <label htmlFor="v10-intake-business-name">Business name</label>
        <input
          id="v10-intake-business-name"
          value={intake.businessName}
          onChange={(event) => intake.setBusinessName(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
          required
          autoComplete="organization"
        />
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-phone">Primary phone</label>
        <input
          id="v10-intake-phone"
          value={intake.primaryPhone}
          onChange={(event) => intake.setPrimaryPhone(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
          autoComplete="tel"
        />
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-email">Primary email</label>
        <input
          id="v10-intake-email"
          type="email"
          value={intake.primaryEmail}
          onChange={(event) => intake.setPrimaryEmail(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
          autoComplete="email"
        />
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-services">Services (one per line)</label>
        <textarea
          id="v10-intake-services"
          value={intake.servicesText}
          onChange={(event) => intake.setServicesText(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
        />
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-service-areas">Service areas (one per line)</label>
        <textarea
          id="v10-intake-service-areas"
          value={intake.serviceAreasText}
          onChange={(event) => intake.setServiceAreasText(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
        />
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-evidence">Primary evidence type</label>
        <select
          id="v10-intake-evidence"
          value={intake.primaryEvidenceType}
          onChange={(event) => intake.setPrimaryEvidenceType(event.target.value as PrimaryEvidenceType | "")}
          onBlur={() => intake.flushDraftOnBlur()}
        >
          <option value="">Select evidence type (optional)</option>
          {PRIMARY_EVIDENCE_TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-evidence-files">Supporting files (photos, PDFs)</label>
        <input
          id="v10-intake-evidence-files"
          type="file"
          multiple
          accept="image/*,.pdf"
          onChange={(event) => {
            intake.attachEvidenceFiles(event.target.files);
            event.target.value = "";
          }}
        />
        {intake.evidenceFiles.length > 0 ? (
          <ul>
            {intake.evidenceFiles.map((file) => (
              <li key={file.fileId}>
                {file.fileName} ({Math.round(file.sizeBytes / 1024)} KB)
              </li>
            ))}
          </ul>
        ) : (
          <p className="ws-muted">Attach portfolio photos or PDFs — file names are saved with your draft.</p>
        )}
      </div>
      <div className="ws-field">
        <label htmlFor="v10-intake-description">Short description</label>
        <textarea
          id="v10-intake-description"
          value={intake.description}
          onChange={(event) => intake.setDescription(event.target.value)}
          onBlur={() => intake.flushDraftOnBlur()}
        />
      </div>
      {intake.view.primaryEvidenceType ? (
        <p className="ws-muted">Current evidence: {toPrimaryEvidenceTypeLabel(intake.view.primaryEvidenceType)}</p>
      ) : null}
      <button
        type="button"
        className="ws-btn ws-btn-primary"
        data-testid="intake-submit"
        onClick={() => void intake.submit()}
        disabled={intake.submitting || intake.businessName.trim().length === 0}
      >
        {intake.submitting ? "Submitting…" : "Submit intake"}
      </button>
    </section>
  );
}
